import { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setReviewCreationKey,
  useGridDefinition,
  useReviewCreationV1Data,
  useSelectReviewCreationCalibrationAndDeliverables,
} from 'reducers/reviewCreationV1Slice';
import { isScoreNameValid } from 'screens/admin/Reviews/ReviewProcessV1/Steps/Calibration/TextScoreSettingTable';

import { isScoreValidator } from 'uiToolkitV1/ScoreSettingTable/ScoreSettingTable';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { ReviewCreationSidepanel } from '../../components/ReviewCreationSidepanel';
import {
  useInvalidateCalibrationAndDeliverables,
  useUpdateReviewsCalibrationAndDeliverables,
} from '../../dataHooks/calibrationAndDeliverablesSectionHooks';
import { PreviewResultButton } from '../../sections/CalibrationAndDeliverables';
import { OutcomeScore } from './sections/OutcomeScore';
import { OverallScore } from './sections/OverallScore';
import { PerformanceGrid } from './sections/PerformanceGrid';

const Header = () => (
  <Flex p="8px 12px 4px 2px" justifyContent="space-between" alignItems="center">
    <TextBodyMedium bold>Calibration & deliverables</TextBodyMedium>
    {/* <PreviewResultButton /> */}
  </Flex>
);

const CardHeader = ({ heading }: { heading: string }) => (
  <Box flex={1} pb={2} borderBottom="1px solid" borderColor="border.CFD7DF">
    <TextBodyMedium bold py={2}>
      {heading}
    </TextBodyMedium>
  </Box>
);

const saveValidator = ({ calibrationAndDeliverables, isCalibrationQuestionDisabled, isOutcomeQuestionDisabled }) => {
  const isValidCalibrationScoreNames = isScoreNameValid(calibrationAndDeliverables?.calibrationQuestion?.answerOptions);
  const isCalibrationScoreValid = isScoreValidator(calibrationAndDeliverables?.calibrationQuestion?.answerOptions);

  const isCalibrationScoreAndNameValid =
    calibrationAndDeliverables?.calibrationQuestion?.answerType === 'DROP_DOWN'
      ? isCalibrationScoreValid && isValidCalibrationScoreNames
      : true;

  const isValidOutcomeScoreNames = isScoreNameValid(calibrationAndDeliverables?.outcomeQuestion?.answerOptions);

  if (isCalibrationQuestionDisabled && isOutcomeQuestionDisabled) {
    return false;
  }
  // if calibration question is disabled, we don't need to validate the calibration score
  if (isCalibrationQuestionDisabled) {
    return !isValidOutcomeScoreNames;
  }
  // if outcome question is disabled, we don't need to validate the outcome score
  if (isOutcomeQuestionDisabled) {
    return !isCalibrationScoreAndNameValid;
  }

  return !isCalibrationScoreAndNameValid || !isValidOutcomeScoreNames;
};

const CalibrationAndDeliverablesPanel = () => {
  const dispatch = useDispatch();
  const { isCalibrationQuestionDisabled, isOutcomeQuestionDisabled, ...calibrationAndDeliverables } =
    useSelectReviewCreationCalibrationAndDeliverables();
  const gridDefinition = useGridDefinition();

  const isSaveDisabled = useMemo(
    () =>
      saveValidator({
        calibrationAndDeliverables,
        isCalibrationQuestionDisabled,
        isOutcomeQuestionDisabled,
      }),
    [calibrationAndDeliverables, isCalibrationQuestionDisabled, isOutcomeQuestionDisabled]
  );

  const invalidateCalibrationAndDeliverables = useInvalidateCalibrationAndDeliverables();

  const { isCalibrationAndDeliverablesPanelOpen } = useReviewCreationV1Data();

  const handleClose = () => {
    invalidateCalibrationAndDeliverables();
    dispatch(setReviewCreationKey({ isCalibrationAndDeliverablesPanelOpen: false }));
  };

  const { mutate: updateCalibrationAndDeliverables } = useUpdateReviewsCalibrationAndDeliverables();

  const onSave = () => {
    const updatedCalibrationAndDeliverables = {
      ...calibrationAndDeliverables,
      outcomeQuestion: {
        ...calibrationAndDeliverables.outcomeQuestion,
      },
      calibrationQuestion: {
        ...calibrationAndDeliverables.calibrationQuestion,
      },
      gridDefinition,
    };

    if (isCalibrationQuestionDisabled) {
      updatedCalibrationAndDeliverables.calibrationQuestion = null;
    }
    if (isOutcomeQuestionDisabled) {
      updatedCalibrationAndDeliverables.outcomeQuestion = null;
    }

    if (
      !isCalibrationQuestionDisabled &&
      Object.hasOwn(updatedCalibrationAndDeliverables?.calibrationQuestion, 'organizationId')
    ) {
      delete updatedCalibrationAndDeliverables.calibrationQuestion.organizationId;
    }

    if (
      !isOutcomeQuestionDisabled &&
      Object.hasOwn(updatedCalibrationAndDeliverables?.outcomeQuestion, 'organizationId')
    ) {
      delete updatedCalibrationAndDeliverables.outcomeQuestion.organizationId;
    }

    updateCalibrationAndDeliverables({ ...updatedCalibrationAndDeliverables });
    dispatch(setReviewCreationKey({ isCalibrationAndDeliverablesPanelOpen: false }));
  };

  return (
    <ReviewCreationSidepanel
      isOpen={isCalibrationAndDeliverablesPanelOpen}
      onClose={handleClose}
      onSave={onSave}
      isSaveDisabled={isSaveDisabled}
    >
      <Flex py={2} flexDir="column" gap={2} mb={1}>
        <Header />
      </Flex>
      <Flex borderRadius="8px" flexDir="column" border="4px solid #E7EBEF" bg="white">
        <Flex flexDir="column" py={3} px={3} gap={3}>
          <CardHeader heading="Calibration" />
          <OverallScore />
          <OutcomeScore />
          <PerformanceGrid />
        </Flex>
      </Flex>
      {/* <Flex borderRadius="8px" m="1" flexDir="column" border="4px solid #E7EBEF" bg="white">
        <Flex flexDir="column" py={2} px={3} gap={6}>
          <CardHeader heading="Deliverables" />
        </Flex>
      </Flex> */}
    </ReviewCreationSidepanel>
  );
};

export { CalibrationAndDeliverablesPanel };
